import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { CampaignList } from './pages/Campaigns/CampaignList';
import { CampaignCreate } from './pages/Campaigns/CampaignCreate';
import { CampaignResults } from './pages/Campaigns/CampaignResults';
import { TemplateList } from './pages/Templates/TemplateList';
import { ReportedMessageList } from './pages/ReportedMessages/ReportedMessageList';
import { AssessmentList } from './pages/Assessments/AssessmentList';
import { AssessmentEvidence } from './pages/Assessments/AssessmentEvidence';
import { ComparisonAnalytics } from './pages/Analytics/ComparisonAnalytics';
import { AtRiskRecipients } from './pages/Analytics/AtRiskRecipients';
import { Reports } from './pages/Analytics/Reports';
import { GroupList } from './pages/Groups/GroupList';
import { PageList } from './pages/Pages/PageList';
import { SMTPList } from './pages/SendingProfiles/SMTPList';
import { SMSProviderList } from './pages/SMSProviders/SMSProviderList';
import { VoiceProviderList } from './pages/VoiceProviders/VoiceProviderList';
import { SendingDomainList } from './pages/SendingDomains/SendingDomainList';
import { SuppressionList } from './pages/Suppressions/SuppressionList';
import { Deliverability } from './pages/Deliverability/Deliverability';
import { Settings } from './pages/Settings/Settings';
import { Platform } from './pages/Platform/Platform';
import { WebhookList } from './pages/Webhooks/WebhookList';
import { ScenarioList } from './pages/Scenarios/ScenarioList';
import { useAuthStore } from './store/authStore';
import { WorkspaceAccess } from './components/auth/WorkspaceAccess';
import { SessionBootstrap } from './components/auth/SessionBootstrap';
import { queryClient } from './store/queryClient';
import './App.css';

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  if (user?.password_change_required) {
    return <Navigate to="/settings" replace />;
  }
  return <Navigate to="/" replace />;
}

function DashboardRoute() {
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/platform" element={<Platform />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route
        path="/"
        element={
          <WorkspaceAccess>
            <Layout />
          </WorkspaceAccess>
        }
      >
        <Route index element={<DashboardRoute />} />
        <Route path="campaigns" element={<CampaignList />} />
        <Route path="campaigns/new" element={<CampaignCreate />} />
        <Route path="campaigns/:id" element={<CampaignResults />} />
        <Route path="templates" element={<TemplateList />} />
        <Route path="reported-messages" element={<ReportedMessageList />} />
        <Route path="assessments" element={<AssessmentList />} />
        <Route path="assessments/:id/evidence" element={<AssessmentEvidence />} />
        <Route path="analytics/comparison" element={<ComparisonAnalytics />} />
        <Route path="analytics/at-risk" element={<AtRiskRecipients />} />
        <Route path="analytics/reports" element={<Reports />} />
        <Route path="groups" element={<GroupList />} />
        <Route path="pages" element={<PageList />} />
        <Route path="landing_pages" element={<Navigate to="/pages" replace />} />
        <Route path="sending_profiles" element={<Navigate to="/sending-profiles" replace />} />
        <Route path="sending-profiles" element={<SMTPList />} />
        <Route path="sms-providers" element={<SMSProviderList />} />
        <Route path="voice-providers" element={<VoiceProviderList />} />
        <Route path="sending-domains" element={<SendingDomainList />} />
        <Route path="suppressions" element={<SuppressionList />} />
        <Route path="deliverability" element={<Deliverability />} />
        <Route path="settings" element={<Settings />} />
        <Route path="webhooks" element={<WebhookList />} />
        <Route path="scenarios" element={<ScenarioList />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionBootstrap><AppRoutes /></SessionBootstrap>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
